import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ModalFocusService {
	private _document = inject(DOCUMENT);
	private _openers = new WeakMap<HTMLElement, HTMLElement>();

	constructor() {
		const zone = inject(NgZone);
		const destroyRef = inject(DestroyRef);
		zone.runOutsideAngular(() => {
			this._document.addEventListener('show.bs.modal', this._onShow);
			this._document.addEventListener('hide.bs.modal', this._onHide);
			this._document.addEventListener('hidden.bs.modal', this._onHidden);
		});
		destroyRef.onDestroy(() => {
			this._document.removeEventListener('show.bs.modal', this._onShow);
			this._document.removeEventListener('hide.bs.modal', this._onHide);
			this._document.removeEventListener('hidden.bs.modal', this._onHidden);
		});
	}

	private _onShow = (event: Event): void => {
		const modal = event.target;
		if (!(modal instanceof HTMLElement) || !modal.classList.contains('modal') || event.defaultPrevented) return;
		const trigger = (event as Event & { relatedTarget?: HTMLElement }).relatedTarget;
		const opener = trigger?.getClientRects().length ? trigger : this._document.activeElement;
		if (opener instanceof HTMLElement && opener !== this._document.body && !modal.contains(opener)) {
			this._openers.set(modal, opener);
		}
	};

	private _onHide = (event: Event): void => {
		const modal = event.target;
		const focused = this._document.activeElement;
		if (event.defaultPrevented || !(modal instanceof HTMLElement) || !modal.classList.contains('modal')) return;
		if (focused instanceof HTMLElement && modal.contains(focused)) focused.blur();
	};

	private _onHidden = (event: Event): void => {
		const modal = event.target;
		if (!(modal instanceof HTMLElement) || !modal.classList.contains('modal')) return;
		const opener = this._openers.get(modal);
		this._openers.delete(modal);
		const focused = this._document.activeElement;
		if (focused && focused !== this._document.body && !modal.contains(focused)) return;
		if (this._document.querySelector('.modal.show')) return;
		if (opener?.isConnected && opener.getClientRects().length && !opener.matches(':disabled, [inert], [inert] *')) {
			opener.focus({ preventScroll: true });
		}
	};
}
